import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { subscriptionGateway } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { formatUtcDate } from '../formatDate';
import { subscriptionStatusLabel } from '../subscriptionStatus';
import { formatCents } from '../types';
import type { CompanySubscriptionResponse } from '../types';

/** WHAT THE USER JUST DID, carried via `navigate(path, { state })` from `SubscriptionPage`'s own wizard.
 * Deliberately only the user's action — never a claim about what it did to the subscription. That outcome
 * (plan selected vs trial started) is always read fresh from the backend below, so a refresh (§23.8 of the
 * approved Subscription Confirmation Page SRS) shows the CURRENT state and never replays an old event. */
export type SubscriptionChangeKind = 'first-selection' | 'upgrade' | 'downgrade';

interface ConfirmationState {
  changeKind?: SubscriptionChangeKind;
  previousPlanName?: string;
}

interface Presentation {
  title: string;
  message: React.ReactNode;
}

/**
 * EPN-79 — the approved Subscription Confirmation Page SRS's one reusable component. "Plan Selected" and "Trial
 * Started" are SEPARATE states (SRS §7/§8), so the headline comes from the server's `effectiveState`, never from the
 * fact that a plan was just chosen. EPN-105: a trial now starts only when an authorized user starts it
 * (`POST /api/billing/trial`), which lands here as `TrialActive`; paid activation arrives through the checkout-return
 * page (`/settings/billing/checkout-complete`), which confirms it against the server before showing anything.
 *
 * Deliberately NOT built here: receipts, invoices and next-billing dates — those live in the billing center, where
 * the server's own invoice and billing-status data is shown.
 */
export function SubscriptionConfirmationPage() {
  const location = useLocation();
  const confirmationState = (location.state as ConfirmationState | null) ?? null;

  const [subscription, setSubscription] = useState<CompanySubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    subscriptionGateway
      .getCurrentSubscription(controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setSubscription(result);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(friendlyErrorMessage(err, 'Could not load your subscription.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  if (loading) return <section className="card card--wide"><p className="muted" role="status">Loading…</p></section>;
  if (error) return <section className="card card--wide"><p className="error" role="alert">{error}</p></section>;
  if (!subscription) {
    return (
      <section className="card card--wide" aria-labelledby="subscription-confirmation-heading">
        <h2 id="subscription-confirmation-heading">No Subscription Yet</h2>
        <p className="muted">This company hasn't selected a plan yet.</p>
        <Link to="/settings/subscription">Choose a plan</Link>
      </section>
    );
  }

  const presentation = presentationFor(subscription, confirmationState);
  const showTrialDates = subscription.trialStartUtc !== null;

  return (
    <section className="card card--wide" aria-labelledby="subscription-confirmation-heading">
      <div className="section-header">
        <h2 id="subscription-confirmation-heading">{presentation.title}</h2>
      </div>

      <p>{presentation.message}</p>

      <dl aria-label="Subscription details">
        <div><dt>Plan</dt><dd>{subscription.planName}</dd></div>
        <div><dt>Subscription status</dt><dd>{subscriptionStatusLabel(subscription)}</dd></div>
        {subscription.effectiveState === 'TrialNotStarted' && (
          <>
            <div><dt>Trial eligibility</dt><dd>{subscription.trialLengthDays}-day free trial — starts when you choose to start it</dd></div>
            <div><dt>Payment method</dt><dd>Not required to start the trial</dd></div>
          </>
        )}
        {showTrialDates && (
          <>
            <div><dt>Trial start date</dt><dd>{formatUtcDate(subscription.trialStartUtc)}</dd></div>
            <div><dt>Trial end date</dt><dd>{formatUtcDate(subscription.trialEndUtc)}</dd></div>
          </>
        )}
        <div><dt>Monthly base fee</dt><dd>{formatCents(subscription.baseMonthlyFeeCents)}</dd></div>
        <div><dt>Per-employee fee</dt><dd>{formatCents(subscription.perEmployeeMonthlyFeeCents)}</dd></div>
      </dl>

      <p className="muted">
        Selecting or changing a subscription plan does not automatically activate live payroll. Live payroll,
        Direct Deposit, contractor payments, and tax payments require completion of the applicable activation,
        verification, authorization, and payroll-readiness requirements.
      </p>

      <div className="inline-form">
        <Link to="/dashboards/admin-dashboard"><button type="button">Go to Dashboard</button></Link>
        <Link to="/settings/subscription" className="link">
          {subscription.effectiveState === 'TrialExpired' ? 'View Plans' : 'View Subscription'}
        </Link>
        <Link to="/settings/billing" className="link">Billing</Link>
      </div>
    </section>
  );
}

function presentationFor(subscription: CompanySubscriptionResponse, state: ConfirmationState | null): Presentation {
  const plan = <strong>{subscription.planName}</strong>;
  const changeKind = state?.changeKind;
  const changedFrom = state?.previousPlanName
    ? <>Your subscription changed from {state.previousPlanName} to {plan}.</>
    : <>Your subscription is now {plan}.</>;

  switch (subscription.effectiveState) {
    case 'TrialNotStarted':
      return {
        title: 'Your Plan Has Been Selected',
        message: changeKind === 'first-selection' || !changeKind
          ? <>You selected {plan}. Your trial has not started yet — start your {subscription.trialLengthDays}-day free trial whenever you are ready. No credit card is needed.</>
          : <>{changedFrom} Your trial has not started yet — start your {subscription.trialLengthDays}-day free trial whenever you are ready.</>,
      };

    case 'TrialActive':
      if (changeKind === 'upgrade' || changeKind === 'downgrade') {
        return {
          title: changeKind === 'upgrade' ? 'Your Plan Has Been Updated' : 'Your Plan Has Been Changed',
          message: <>{changedFrom} Your trial dates are unchanged.{changeKind === 'downgrade' && ' This takes effect immediately — no proration or partial-month billing is calculated in this build.'}</>,
        };
      }
      return {
        title: changeKind === 'first-selection' ? 'Your 30-Day Free Trial Has Started' : 'Your Free Trial Is Active',
        message: <>Your {plan} trial is active. You can explore setup, employee/contractor management, payroll preview, and available plan features during your trial.</>,
      };

    case 'TrialExpired':
      return {
        title: 'Your Trial Has Ended',
        message: <>Your {subscription.trialLengthDays}-day {plan} trial has ended. Your setup data remains saved. Subscribe to continue using paid Escochex features.</>,
      };

    case 'Active':
      return {
        title: changeKind ? 'Your Plan Has Been Updated' : 'Current Subscription',
        message: changeKind ? changedFrom : <>Your {plan} subscription is active.</>,
      };

    case 'PendingChange':
      return {
        title: 'Current Subscription',
        message: <>Your {plan} subscription is active. A plan change is scheduled for the end of the current billing period.</>,
      };

    case 'PastDueGrace':
      return {
        title: 'Payment Failed',
        message: <>Your last payment did not go through. Your {plan} access continues for now — update your payment method to keep it.</>,
      };

    case 'PastDueRestricted':
      return {
        title: 'Payment Failed',
        message: <>Your last payment did not go through, so the company is read-only until the payment is collected. Update your payment method to restore full access.</>,
      };

    case 'Canceled':
      return {
        title: 'Subscription Canceled',
        message: <>Your {plan} subscription is canceled and the company is read-only. You can reactivate it from the billing page.</>,
      };

    default:
      return {
        title: 'Subscription Status',
        message: <>Your {plan} subscription is {subscriptionStatusLabel(subscription).toLowerCase()}.</>,
      };
  }
}
